import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Dropdown, notification } from 'antd';

import { getTerminationClaimPdf } from 'store/termination/actions';
import { getTerminationsDetailsTable } from 'api/contracts';

import DateLabel from 'components/DateLabel';
import { MenuIcon } from 'components/Icons';
import TableTools from './TableTools';
import Table from 'components/Table';
import columnsList from './columns';
import Chip from '../../../Chip';

import { moneyFormatter } from 'utils/helpers';
import { ROUTES } from 'constants/routes';

import styles from '../ContractDetailsTables.module.less';
import { checkPermissions } from 'roles/userCan';
import { useCurrency } from '../../../../hooks/useCurrency';
import { AppState } from 'store/reducer';
import { ContractTerminationDetailsDto } from 'types/dto/contracts-service';

interface ITableProps {
  contractId: number;
}

export default ({ contractId }: ITableProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { currentCompanyCurrency } = useSelector((store: AppState) => ({
    currentCompanyCurrency: store.user.currentCompanyCurrency,
  }));

  const { currency } = useCurrency();

  const [terminationDetails, setTerminationDetails] = useState<ContractTerminationDetailsDto | null>(null);
  useEffect(() => {
    getTerminationsDetailsTable(contractId)
      .then(res => {
        setTerminationDetails(res.data);
      })
      .catch(() => {
        notification.error({
          message: t('contract_details.error_terminations'),
          description: t('contract_details.loading_information_terminations'),
        });
      });
  }, []);

  const columns = columnsList(t).map(({ title, value }) => {
    const basicColumn = {
      title: title,
      dataIndex: value,
      key: value,
    };
    switch (value) {
      case 'terminationNumber': {
        return {
          ...basicColumn,
          width: 250,
        };
      }
      case 'realEndDate':
      case 'terminationDate':
      case 'signDate': {
        return {
          ...basicColumn,
          render: (date: any) => <DateLabel>{date}</DateLabel>,
        };
      }
      case 'cause': {
        return {
          ...basicColumn,
          width: 300,
        };
      }
      case 'status': {
        return {
          ...basicColumn,
          width: 135,
          render: (id: number, record: any) => {
            return !!record.signDate ? (
              <Chip color={'success'} label={t('contract_details.signed_terminations')} />
            ) : (
              <Chip color={'warn'} label={t('contract_details.draft_terminations')} />
            );
          },
        };
      }
      case 'calculatedRefundSum': {
        return {
          ...basicColumn,
          render: (value: number) => `${moneyFormatter(value)} ${currentCompanyCurrency || currency}`,
        };
      }
      case 'actions': {
        return {
          ...basicColumn,
          title: '',
          render: (id: number, record: any) => {
            return (
              <div onClick={e => e.stopPropagation()}>
                <Dropdown
                  overlay={() => (
                    <TableTools
                      id={id}
                      record={record}
                      handleDownload={() => dispatch(getTerminationClaimPdf(Number(record.id), t))}
                    />
                  )}
                  placement="bottomRight"
                  trigger={['hover']}
                >
                  <span className="ant-dropdown-link">
                    <MenuIcon />
                  </span>
                </Dropdown>
              </div>
            );
          },
          width: 50,
          fixed: 'right' as 'right',
        };
      }
      default: {
        return basicColumn;
      }
    }
  });

  const onRowClick = useCallback(rowId => {
    history.push(`${ROUTES.CONTRACTS_LIST.CONTRACT.ROOT}/${contractId}/terminations/${rowId}`);
  }, []);

  return (
    <Table
      onRow={record => ({
        onClick: () => {
          checkPermissions('TERMINATION', 'READ') && onRowClick(record.id);
        },
      })}
      className={styles.table}
      columns={columns}
      pagination={false}
      scroll={{ x: 1280 }}
      rowKey={(record: any) => `${record.actions}`}
      dataSource={terminationDetails ? [terminationDetails] : []}
      bordered
    />
  );
};
